import _ from "underscore";

import Persistence from "pages/common/lib/grabyo-persistence";
import Constants from "const/constants";

import BrightcoveCredential from "../model/credentials/brightcove";
import FacebookCredential from "../model/credentials/facebook";
import UnsupportedCredential from "../model/credentials/unsupported";
import TwitterCredential from "../model/credentials/twitter";
import YouTubeCredential from "../model/credentials/youtube";
import SpredfastCredential from "../model/credentials/spredfast";
import ThePlatformCredential from "../model/credentials/theplatform";
import OptaCredential from "../model/credentials/opta";
import TikTokCredential from "../model/credentials/tiktok";
import InstagramCredential from "../model/credentials/instagram";
import FtpCredential from "../model/credentials/ftp";
import S3Credential from "../model/credentials/s3";
import GloboCredential from "../model/credentials/globo";
import GoogleCloudStorageCredential from "../model/credentials/google-cloud-storage";
import TwitchCredential from "../model/credentials/twitch";
import ThreadsCredential from "../model/credentials/threads";

import BaseCollection from "./base";

export function socialCredentialsModelConstructor(attrs) {
    switch (attrs.credentialType) {
        case `brightcove`:
            return BrightcoveCredential;
        case `facebook`:
        case `facebook_page`:
            return FacebookCredential;
        case `twitter`:
            return TwitterCredential;
        case `youtube`:
            return YouTubeCredential;
        case `spredfast`:
            return SpredfastCredential;
        case `theplatform`:
            return ThePlatformCredential;
        case `instagram`:
            return InstagramCredential;
        case `opta`:
            return OptaCredential;
        case `ftp`:
            return FtpCredential;
        case `s3`:
            return S3Credential;
        case `twitch`:
            return TwitchCredential;
        case `tiktok`:
            return TikTokCredential;
        case `threads`:
            return ThreadsCredential;
        case `globo`:
            return GloboCredential;
        case `google_cloud_platform`:
            return GoogleCloudStorageCredential;
        default:
            return UnsupportedCredential;
    }
}

var SocialCredentialCollection = BaseCollection.extend({
    url: function () {
        return `${Constants.DEFAULT_API_PATH}/account/social-credentials`;
    },

    parse: function (response) {
        return _.isArray(response) ? response : response.data;
    },

    model: function (attrs, options) {
        return new (socialCredentialsModelConstructor(attrs))(attrs, options);
    },

    modelId: function (attrs) {
        return attrs.socialId;
    },

    initialize: function (models, options) {
        options = _.isObject(options) ? options : {};

        this.user = options.user;

        _.bindAll(this, `fetch`);

        if (options.listenToNewCredentials === true) {
            this.listenTo(App.Events, `new:socialCredential`, this.fetch);
        }
    },

    comparator: function (a, b) {
        if (a.get(`name`) && String.prototype.localeCompare) {
            return a.get(`name`).localeCompare(b.get(`name`));
        } else {
            return a.get(`name`) > b.get(`name`) ? 1 : -1;
        }
    },

    isTypeEnabled: function (type) {
        return !_.isUndefined(
            this.findWhere({
                type,
                enabled: true,
                status: `active`
            })
        );
    },

    hasType: function (type) {
        type = _.isString(type) ? type : ``;
        return (
            this.filter(function (cred) {
                return (
                    type.indexOf(cred.get(`type`)) === 0 &&
                    cred.get(`status`) === `active`
                );
            }).length > 0
        );
    },

    getEnabledCredentials: function () {
        return this.where({
            enabled: true,
            status: `active`
        });
    },

    getEnabledCredentialsForType: function (type, options) {
        options = _.isObject(options) ? options : {};
        var activeCredentials = this.where({
            enabled: true,
            status: `active`,
            type
        });
        if (options.rawClip === true) {
            var allowedTypes = [`facebook`, `youtube`, `twitter`];
            return activeCredentials.filter(function (credential) {
                return _.contains(allowedTypes, credential.get(`type`));
            });
        } else {
            return activeCredentials;
        }
    },

    countEnabledCredentials: function () {
        var socialCredentialsCount = this.getEnabledCredentials().length;
        var galleryEnabled = Persistence.PermStore.get(`galleryEnabled`);
        return socialCredentialsCount + (galleryEnabled === true ? 1 : 0);
    },

    /**
     * This counts the number of credential activated on the user
     * But only for the social platform that requires a social message
     */
    countEnabledCredentialsForSocialMessage: function () {
        var enabledCredentials = this.getEnabledCredentials();
        return enabledCredentials.filter(function (credential) {
            if (
                credential.get(`type`) === `twitter` ||
                credential.get(`type`) === `facebook`
            ) {
                return true;
            } else {
                return false;
            }
        }).length;
    },

    getActiveSocialCredentialsForClipSharing: function () {
        return this.filter(function (credential) {
            return (
                credential.isClipSharingEnabledForType() &&
                !credential.isDeprecated() &&
                (credential.isActive() || credential.isInvalid())
            );
        });
    },

    getEnabledSocialCredentialsForClipSharing: function () {
        return this.filter(
            (credential) =>
                credential.isActive() &&
                !credential.isDeprecated() &&
                credential.get(`enabled`) === true &&
                credential.isClipSharingEnabledForType()
        );
    },

    toggleSocialCredentialsForClipSharing: function () {
        const data = {};
        const socialIds =
            this.getAllSocialIdsForEnabledCredentialsForClipSharing();
        data.socialIds = socialIds.join(`,`);
        $.ajax({
            url: `${this.url()}/toggle`,
            type: `PUT`,
            processData: false,
            contentType: `application/json`,
            data
        })
            .done(this.fetch)
            .fail(function () {
                App.Events.trigger(
                    `fail:toggleStatusMultipleSocialAccounts`,
                    socialIds
                );
            });
    },

    toggleEnabledSocialCredentialsForClipSharing: function (socialIds) {
        socialIds.forEach(
            function (socialId) {
                const cred = this.findWhere({ socialId });
                cred.set(`enabled`, false);
            }.bind(this)
        );
    },

    // Updates model silently to prevent useSocialCredentialsModel from overriding rss credential
    updateSelectedSocialCredentialsForClipSharing: function (
        socialIds,
        enabled
    ) {
        this.each(function (credential) {
            const enableCred =
                socialIds?.length &&
                socialIds.includes(credential.get("socialId"));

            credential.set("enabled", enableCred ? enabled : false, {
                silent: true
            });
        });
    },

    getAllSocialIdsForEnabledCredentialsForClipSharing: function () {
        const enabledCreds = this.getEnabledSocialCredentialsForClipSharing();
        return enabledCreds.map((cred) => cred.get(`socialId`));
    },

    getActiveSocialCredentialsForLiveStreaming: function () {
        return this.filter(function (credential) {
            return (
                credential.isLiveStreamingEnabledForType() &&
                (credential.isActive() || credential.isInvalid())
            );
        });
    },

    getCredentialsByType: function (type) {
        return this.where({ type });
    },

    getActiveSocialCredentials: function () {
        return this.where({ status: `active` });
    },

    updateExportedEncodeRecords: function () {
        this.each(function (record) {
            const credentialId = record.get(`userSocialCredentialId`);
            const credential = this.get(credentialId);
            if (credential) {
                credential.set(`exportedEncodeRecord`, record);
            }
        }, this);
    },

    /**
     * This return the smallest maximum duration for a given social credential type
     * It is needed for Twitter Native that can accept clips that are 2mn20 or 10mn
     * @param  {String} type [social credential type we target]
     * @return {Number}      [duration in ms]
     */
    getClipMaxDuration: function (type) {
        return Math.min.apply(
            this,
            this.where({
                type,
                status: `active`,
                enabled: true
            }).map((cred) => cred.getClipMaxDuration())
        );
    }
});

export default SocialCredentialCollection;
